import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth } from '@/redux/auth/selectors';
import { request } from '@/request';
import cryptoHelpers from '@/utils/cryptoGraphy';
import { getObfuscatedPath } from '@/utils/apiRoute';
export const useInitiativeData = () => {
  const [initiatives, setInitiatives] = useState([]);
  const obfuscatedPath = getObfuscatedPath('initiatives');
  const initiativeData = async () => {
    // const obfuscatedPath = getObfuscatedPath('initiatives');
    try {
      const { result: result } = await request.get({ entity: `${obfuscatedPath}/getAll` });
      // const decryptedData = await cryptoHelpers.decrypt(encryptedResponse);
      setInitiatives(result);
    } catch (err) {
      console.error('Error fetching or decrypting initiatives:', err);
      setInitiatives([]);
    }
  };

  useEffect(() => {
    initiativeData();
  }, []);
  // Ensure initiatives is always an array
  return initiatives;
};
// export const useSubsectorData = () => {
//   const [subsectors, setSubsectors] = useState([]);
//   const fetchSubsectorData = async () => {
//     try {
//       const { result: subsectorResult } = await request.get({ entity: 'subsector' });
//       const decryptedResult=cryptoHelpers.decrypt(subsectorResult);
//       setSubsectors(decryptedResult);
//     } catch (err) {
//       console.error('Error fetching users:', err);
//     }
//   };
//   useEffect(() => {
//     fetchSubsectorData();
//   }, []);
//   return subsectors;
// };
export const useInitiativesData = () => { // Default to true if not provided
  const [initiatives, setInitiatives] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (initiatives.length > 0 || isLoading || error) {
        setInitiatives([]);
        setIsLoading(false);
        setError(null);
      }


      // If shouldFetch is true, proceed with fetching
      setIsLoading(true);
      setError(null); // Clear any previous errors

      try {
        const obfuscatedPath = getObfuscatedPath('initiatives');
        const { result: result } = await request.get({ entity: `${obfuscatedPath}/getAll` });
        // const decryptedData = await cryptoHelpers.decrypt(encryptedResponse);
        setInitiatives(result);
      } catch (err) {
        console.error('Error fetching or decrypting initiatives:', err);
        setError(err);
        setInitiatives([]); // Ensure it's an array even on error
      } finally {
        setIsLoading(false); // Always set loading to false after fetch attempt
      }
    };

    fetchData();
  }, []); // Effect re-runs when 'shouldFetch' changes

  // Always return an object with 'initiatives' (guaranteed to be an array), 'isLoading', and 'error'.
  return { initiatives, isLoading, error };
};
export const useSubsectorData = () => {
  const [subsectors, setSubsectors] = useState([]);

  useEffect(() => {
    // 1. Create a flag
    let isMounted = true; 

    const fetchSubsectorData = async () => {
      try {
        const { result: subsectorResult } = await request.get({ entity: 'subsector' });
        
        // 2. Only update state if the component is still mounted
        if (isMounted) {
          setSubsectors(subsectorResult);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching subsectors:', err);
        }
      }
    };

    fetchSubsectorData();

    // 3. Cleanup function sets flag to false when component unmounts
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array means this runs once on mount

  return subsectors;
};
export const useFilterActionData = (filterType, key, value, sortField, sortOrder) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchData = useCallback(async () => {
    // If filterType is not provided, do not make an API call.
    // This allows the backend to handle the case where no specific filter is active.
    if (!filterType) {
      setData([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = {
        ...(sortField &&
          sortOrder && {
          _sortBy: sortField,
          _order: sortOrder === 'ascend' ? 'asc' : 'desc',
        }),
        currentFilter: filterType,
      };

      // Consistently send key and value, even if they are null/undefined
      // This allows the backend to decide how to interpret a missing/empty filter
      if (key !== null && key !== undefined) {
        params.key = key;
        if (value !== null && value !== undefined) {
          // Ensure value is always an array for consistency with backend expectation
          // based on your `filterAll` function's `Array.isArray(value)` checks.
          params.value = Array.isArray(value) ? value : [value];
        } else {
          // If value is null/undefined, send it as null to the backend
          params.value = null;
        }
      } else {
        // If key is null/undefined, send key and value as null
        params.key = null;
        params.value = null;
      }

      const obfuscatedPath = getObfuscatedPath('workstreamaction'); // Using 'workstreamaction' as per your original code
      const response = await request.filterALL({ entity: `${obfuscatedPath}/filterAll`, params });

      let fetchedAction = [];
      let fetchedTotalCount = 0;

      // Consistent response handling logic
      if (response && response.success) { // Check for `success` property from your backend response
        const decryptedResult = cryptoHelpers.decrypt(response.result);

        if (Array.isArray(decryptedResult)) {
          fetchedAction = decryptedResult;
          fetchedTotalCount = response.totalCount || decryptedResult.length;
        } else if (decryptedResult && Array.isArray(decryptedResult.workstreamactions)) {
          // This branch handles a nested object structure like { workstreamactions: [], totalCount: N }
          fetchedAction = decryptedResult.workstreamactions;
          fetchedTotalCount = decryptedResult.totalCount || decryptedResult.workstreamactions.length;
        } else {
          console.warn('useFilterActionData: Unexpected decrypted data structure:', decryptedResult);
        }
      } else {
        // Handle cases where response.success is false or response is null/undefined
        console.warn('useFilterActionData: Backend response indicates failure or is malformed:', response);
      }

      setData(fetchedAction);
      setTotalCount(fetchedTotalCount);

    } catch (err) {
      console.error('useFilterActionData: Error fetching actions:', err);
      setError(err);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filterType, key, value, sortField, sortOrder, getObfuscatedPath, request, cryptoHelpers]); // Added dependencies

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, totalCount };
};
export const useFilterInitiativeData = (filterType, key, value, sortField, sortOrder) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const fetchData = useCallback(async () => {
    if (!filterType) {
      setData([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        ...(sortField &&
          sortOrder && {
          _sortBy: sortField,
          _order: sortOrder === 'ascend' ? 'asc' : 'desc',
        }),
        currentFilter: filterType,
      };
      if (key !== null && key !== undefined) {
        params.key = key;
        if (value !== null && value !== undefined) {
          params.value = Array.isArray(value) ? value : [value];
        } else {
          params.value = null;
        }
      } else {
        params.key = null;
        params.value = null;
      }
      const obfuscatedPath = getObfuscatedPath('initiatives');
      const response = await request.filterALL({ entity: `${obfuscatedPath}/filterAll`, params });
      let fetchedInitiatives = [];
      let fetchedTotalCount = 0;
      if (response && Array.isArray(cryptoHelpers.decrypt(response.result))) {
        fetchedInitiatives = cryptoHelpers.decrypt(response.result);
        fetchedTotalCount = response.totalCount || fetchedInitiatives.length;
      } else if (response && cryptoHelpers.decrypt(response.result) && Array.isArray(cryptoHelpers.decrypt(response.result.initiatives))) {
        fetchedInitiatives = cryptoHelpers.decrypt(response.result.initiatives);
        fetchedTotalCount = response.result.totalCount || fetchedInitiatives.length;
      } else if (response && Array.isArray(response)) {
        fetchedInitiatives = response;
        fetchedTotalCount = response.totalCount || fetchedInitiatives.length;
      } else {
        console.warn('useFilterInitiativeData: Unexpected data structure from backend:', response);
      }
      setData(fetchedInitiatives);
      setTotalCount(fetchedTotalCount);
    } catch (err) {
      console.error('useFilterInitiativeData: Error fetching initiatives:', err);
      setError(err);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filterType, key, value, sortField, sortOrder, getObfuscatedPath, request, cryptoHelpers]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  return { data, isLoading, error, totalCount };
};
export const useAuditHistoryData = () => {
  const [auditHistory, setAuditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true); // To track if there are more pages to load

  const fetchHistory = useCallback(async () => {
    if (loadingHistory || !hasMore) return; // Prevent multiple simultaneous fetches or fetching if no more data

    setLoadingHistory(true);
    try {
      const obfuscatedPath = getObfuscatedPath('history');
      const response = await request.get({ entity: obfuscatedPath, params: { page, pageSize: 10 } });
      const decryptedData = response.result;

      if (decryptedData) {
        setAuditHistory((prevHistory) => [...prevHistory, ...decryptedData]);
        setHasMore(response.hasMore);
        setPage((prevPage) => prevPage + 1);
      } else {
        console.warn('No history data found or empty response:', decryptedData?.message || 'No response message');
        setHasMore(false); // No data, so no more to fetch
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      setHasMore(false); // On error, assume no more data can be fetched
    } finally {
      setLoadingHistory(false);
    }
  }, [page, loadingHistory, hasMore]); // Dependencies for useCallback

  useEffect(() => {
    fetchHistory();
  }, []); // Initial fetch when component mounts

  return { auditHistory, loadingHistory, hasMore, fetchHistory }; // Return fetchHistory to allow triggering more loads
};
export const useUserData = () => {
  const [users, setUsers] = useState([]);
  const fetchUserData = async () => {
    try {
      const obfuscatedPath = getObfuscatedPath('user');
      const { result: userResult } = await request.get({ entity: obfuscatedPath });
   
      const decryptedData = await cryptoHelpers.decrypt(userResult);
      setUsers(decryptedData);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };
  useEffect(() => {
    fetchUserData();
  }, []);
  return users;
};
export const useWorkstreamData = () => {
  const [workstreams, setWorkstreams] = useState([]);
  const fetchWorkstreamData = async () => {
    try {
      const obfuscatedPath = getObfuscatedPath('subworkstream');
      const { result: workstreamResult } = await request.get({ entity: obfuscatedPath });
      const decryptedData = workstreamResult;
      setWorkstreams(decryptedData);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };
  useEffect(() => {
    fetchWorkstreamData();
  }, []);
  return workstreams;
};
export const useSearchMilestoneData = (searchKey, searchValue, shouldFetch = true) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchData = useCallback(async () => {
    if (!shouldFetch || !searchKey || !searchValue) {
      setData([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const obfuscatedPath = getObfuscatedPath('initiatives');

      const response = await request.get({
        entity: `${obfuscatedPath}/searchMilestone`,
        params: {
          searchType: 'milestone',
          searchKey,
          searchValue,
        },
      });

      let result = [];
      if (response?.success) {
        const decrypted = cryptoHelpers.decrypt(response.result);
        result = Array.isArray(decrypted) ? decrypted : [];
        setTotalCount(response.total || result.length);
      }

      setData(result);
    } catch (err) {
      setError(err);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchKey, searchValue, shouldFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, totalCount };
};
export const useSearchImpactData = (searchKey, searchValue, shouldFetch = true) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchData = useCallback(async () => {
    if (!shouldFetch || !searchKey || !searchValue) {
      setData([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = {
        searchKey,
        searchValue,
      };

      const obfuscatedPath = getObfuscatedPath('initiatives');
      const response = await request.get({
        entity: `${obfuscatedPath}/searchImpact`,
        params,
      });

      let fetched = [];
      let fetchedTotal = 0;

      if (response?.success) {
        const decrypted = cryptoHelpers.decrypt(response.result);

        if (Array.isArray(decrypted)) {
          fetched = decrypted;
          fetchedTotal = response.total || decrypted.length;
        } else if (Array.isArray(decrypted.initiatives)) {
          fetched = decrypted.initiatives;
          fetchedTotal = response.total || decrypted.initiatives.length;
        } else {
          console.warn('Unexpected structure in decrypted result:', decrypted);
        }
      } else {
        console.warn('Backend returned failure or malformed response:', response);
      }

      setData(fetched);
      setTotalCount(fetchedTotal);
    } catch (err) {
      console.error('useSearchInitiativesData: Error fetching initiatives:', err);
      setError(err);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchKey, searchValue, shouldFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, totalCount };
};
export const useSearchKpiData = (searchKey, searchValue, shouldFetch = true) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchData = useCallback(async () => {
    if (!shouldFetch || !searchKey || !searchValue) {
      setData([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const obfuscatedPath = getObfuscatedPath('initiatives');

      const response = await request.get({
        entity: `${obfuscatedPath}/searchKPI`,
        params: {
          searchKey,
          searchValue,
        },
      });

      let result = [];
      if (response?.success) {
        const decrypted = cryptoHelpers.decrypt(response.result);
        result = Array.isArray(decrypted) ? decrypted : [];
        setTotalCount(response.total || result.length);
      }

      setData(result);
    } catch (err) {
      setError(err);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchKey, searchValue, shouldFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, totalCount };
};
export const useInitiativePaginatedData = (initialLimit = 10, shouldFetch = true) => {
  const [data, setData] = useState([]); // Accumulates all fetched data
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true); // Flag to know if there's more data on the server
  const totalCountRef = useRef(0); // Ref to store total count without causing re-renders
  const fetchData = useCallback(async () => {
    if (!shouldFetch || isLoading || !hasMore) {
      return;
    }

    setIsLoading(true);
    setError(null); // Clear any previous errors before a new fetch
    try {
      const obfuscatedPath = getObfuscatedPath('initiatives');
      const url = `${obfuscatedPath}/getPaginated`;
      const {
        result: encryptedResponse,
        total: backendTotal,
        page: backendPage,
        limit: backendLimit,
      } = await request.get({
        entity: url,
        params: { page: currentPage, limit: initialLimit },
      });

      const decryptedData = await cryptoHelpers.decrypt(encryptedResponse);
      if (decryptedData && Array.isArray(decryptedData)) {
        // Append the new data to the existing list
        setData(prevData => {
          const newData = [...prevData, ...decryptedData];
          return newData;
        });
        totalCountRef.current = backendTotal;

        // Determine if there's more data to fetch
        const loadedItemsCount = (backendPage - 1) * backendLimit + decryptedData.length;
        setHasMore(loadedItemsCount < backendTotal);
      } else {
        setData(prevData => prevData); // Keep existing data
        setHasMore(false); // No more data to fetch
      }

    } catch (err) {
      console.error('Error fetching initiatives:', err);
      setError(err);
      setHasMore(false); // Stop trying to fetch more on error
    } finally {
      setIsLoading(false); // Always stop loading when the request is complete
    }
  }, [currentPage, initialLimit, shouldFetch]); // ⭐ CORRECTED: Removed isLoading and hasMore from dependencies
  useEffect(() => {
    fetchData();
  }, [fetchData]); // This effect will now only run when fetchData itself is recreated (due to its own dependencies changing)
  const loadMore = useCallback(() => {
    // Only increment the page if we're not already loading and there's more data to fetch
    if (!isLoading && hasMore) {
      setCurrentPage(prevPage => {
        return prevPage + 1;
      });
    } else {
    }
  }, [isLoading, hasMore]); // isLoading and hasMore must be dependencies here to ensure latest values are captured
  const resetAndFetch = useCallback(() => {
    setData([]);
    setIsLoading(false); // Reset loading state
    setError(null);      // Clear errors
    setCurrentPage(1);   // Reset to first page
    setHasMore(true);    // Assume there's more data initially
    totalCountRef.current = 0; // Reset total count
  }, []); // This function is stable as it has no external dependencies that change
  useEffect(() => {
    if (shouldFetch) {
      // If shouldFetch becomes true, reset and start fetching from page 1.
      resetAndFetch();
    } else {
      resetAndFetch(); // Reset clears data and sets hasMore to true, but fetchData guard prevents fetch when shouldFetch is false
    }
  }, [shouldFetch, initialLimit, resetAndFetch]); // Include `resetAndFetch` in dependencies
  return {
    data,
    isLoading,
    error,
    hasMore,
    loadMore,
    total: totalCountRef.current,
    currentPage, // Expose for debugging if needed
    resetAndFetch,
  };
};
export const useImpactPaginatedData = (initialLimit = 10, shouldFetch = true) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const totalCountRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!shouldFetch) {
      return;
    }
    if (isLoading || !hasMore) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const obfuscatedPath = getObfuscatedPath('initiatives');
      // ⭐ CORRECTED: Use the endpoint from your new backend controller
      const url = `${obfuscatedPath}/getAllImpact`;
      const {
        result: encryptedResponse,
        total: backendTotal,
        page: backendPage,
        limit: backendLimit,
      } = await request.get({
        entity: url,
        params: { page: currentPage, limit: initialLimit },
      });

      const decryptedData = await cryptoHelpers.decrypt(encryptedResponse);
      if (decryptedData && Array.isArray(decryptedData)) {
        // Append the new data to the existing list
        setData(prevData => [...prevData, ...decryptedData]);
        totalCountRef.current = backendTotal;

        // Determine if there's more data to fetch
        const loadedItemsCount = (backendPage - 1) * backendLimit + decryptedData.length;
        setHasMore(loadedItemsCount < backendTotal);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      setError(err);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, initialLimit, shouldFetch]); // ⭐ CORRECTED: Removed `hasMore` and `isLoading` from dependencies to break the circular dependency.
  useEffect(() => {
    // This useEffect will now only re-run when fetchData's identity changes,
    // which only happens when `currentPage`, `initialLimit`, or `shouldFetch` changes.
    fetchData();
  }, [fetchData]);
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setCurrentPage(prevPage => {
        return prevPage + 1;
      });
    } else {
    }
  }, [isLoading, hasMore]); // These dependencies are correct for `loadMore`
  const resetAndFetch = useCallback(() => {
    setData([]);
    setIsLoading(false);
    setError(null);
    setCurrentPage(1);
    setHasMore(true);
    totalCountRef.current = 0;
  }, []); // No dependencies needed here since it only uses state setters
  useEffect(() => {
    if (shouldFetch) {
      // We need to reset and fetch again when the `shouldFetch` prop becomes true.
      // This ensures the data is re-fetched when the filter becomes visible.
      resetAndFetch();
    } else {
      // If shouldFetch becomes false, reset all states to stop further fetches.
      setData([]);
      setCurrentPage(1);
      setHasMore(true);
      totalCountRef.current = 0;
      setIsLoading(false);
      setError(null);
    }
  }, [shouldFetch, initialLimit, resetAndFetch]);

  // Return the accumulated data, state, and functions for the component to use.
  return {
    data,
    isLoading,
    error,
    hasMore,
    loadMore,
    total: totalCountRef.current,
    currentPage,
    resetAndFetch,
  };
};
export const useMilestonePaginatedData = (initialLimit = 10, shouldFetch = true) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const totalCountRef = useRef(0);
  const fetchData = useCallback(async () => {
    if (!shouldFetch || isLoading || !hasMore) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const obfuscatedPath = getObfuscatedPath('initiatives');
      const url = `${obfuscatedPath}/getAllMilestone`;
      const {
        result: encryptedResponse,
        total: backendTotal,
        page: backendPage,
        limit: backendLimit,
      } = await request.get({
        entity: url,
        params: { page: currentPage, limit: initialLimit },
      });
      const decryptedData = await cryptoHelpers.decrypt(encryptedResponse);
      if (decryptedData && Array.isArray(decryptedData)) {
        setData(prevData => [...prevData, ...decryptedData]);
        totalCountRef.current = backendTotal;
        const loadedItemsCount = (backendPage - 1) * backendLimit + decryptedData.length;
        setHasMore(loadedItemsCount < backendTotal);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('[Milestone Hook] Error fetching milestones:', err);
      setError(err);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, initialLimit, shouldFetch]); // ⭐ CORRECTED: Removed `hasMore` and `isLoading` from dependencies to break the circular dependency.

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setCurrentPage(prevPage => {
        return prevPage + 1;
      });
    } else {
    }
  }, [isLoading, hasMore]); // These dependencies are correct for `loadMore`

  const resetAndFetch = useCallback(() => {
    setData([]);
    setIsLoading(false);
    setError(null);
    setCurrentPage(1);
    setHasMore(true);
    totalCountRef.current = 0;
  }, []);

  useEffect(() => {
    if (shouldFetch) {
      resetAndFetch();
    } else {
      setData([]);
      setCurrentPage(1);
      setHasMore(true);
      totalCountRef.current = 0;
      setIsLoading(false);
      setError(null);
    }
  }, [shouldFetch, initialLimit, resetAndFetch]);

  return {
    data,
    isLoading,
    error,
    hasMore,
    loadMore,
    total: totalCountRef.current,
    currentPage,
    resetAndFetch,
  };
};
export const useKPIPaginatedData = (initialLimit = 10, shouldFetch = true) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const totalCountRef = useRef(0);
  const fetchData = useCallback(async () => {
    if (!shouldFetch || isLoading || !hasMore) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const obfuscatedPath = getObfuscatedPath('initiatives');
      const url = `${obfuscatedPath}/getAllKPI`;
      const {
        result: encryptedResponse,
        total: backendTotal,
        page: backendPage,
        limit: backendLimit,
      } = await request.get({
        entity: url,
        params: { page: currentPage, limit: initialLimit },
      });

      const decryptedData = await cryptoHelpers.decrypt(encryptedResponse);
      if (decryptedData && Array.isArray(decryptedData)) {
        setData(prevData => [...prevData, ...decryptedData]);
        totalCountRef.current = backendTotal;
        const loadedItemsCount = (backendPage - 1) * backendLimit + decryptedData.length;
        setHasMore(loadedItemsCount < backendTotal);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('[KPI Hook] Error fetching KPIs:', err);
      setError(err);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, initialLimit, shouldFetch, hasMore, isLoading]); // ⭐ CORRECTED: Added hasMore and isLoading to dependencies

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setCurrentPage(prevPage => {
        return prevPage + 1;
      });
    } else {
    }
  }, [isLoading, hasMore]);

  const resetAndFetch = useCallback(() => {
    setData([]);
    setIsLoading(false);
    setError(null);
    setCurrentPage(1);
    setHasMore(true);
    totalCountRef.current = 0;
  }, []);

  useEffect(() => {
    if (shouldFetch) {
      resetAndFetch();
    } else {
      setData([]);
      setCurrentPage(1);
      setHasMore(true);
      totalCountRef.current = 0;
      setIsLoading(false);
      setError(null);
    }
  }, [shouldFetch, initialLimit, resetAndFetch]);

  return {
    data,
    isLoading,
    error,
    hasMore,
    loadMore,
    total: totalCountRef.current,
    currentPage,
    resetAndFetch,
  };
};
export const useSearchInitiativesData = (searchKey, searchValue, shouldFetch = true) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchData = useCallback(async () => {
    if (!shouldFetch || !searchKey || !searchValue) {
      setData([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = {
        searchKey,
        searchValue,
      };

      const obfuscatedPath = getObfuscatedPath('initiatives');
      const response = await request.get({
        entity: `${obfuscatedPath}/searchFilter`,
        params,
      });

      let fetched = [];
      let fetchedTotal = 0;

      if (response?.success) {
        const decrypted = cryptoHelpers.decrypt(response.result);

        if (Array.isArray(decrypted)) {
          fetched = decrypted;
          fetchedTotal = response.total || decrypted.length;
        } else if (Array.isArray(decrypted.initiatives)) {
          fetched = decrypted.initiatives;
          fetchedTotal = response.total || decrypted.initiatives.length;
        } else {
          console.warn('Unexpected structure in decrypted result:', decrypted);
        }
      } else {
        console.warn('Backend returned failure or malformed response:', response);
      }

      setData(fetched);
      setTotalCount(fetchedTotal);
    } catch (err) {
      console.error('useSearchInitiativesData: Error fetching initiatives:', err);
      setError(err);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchKey, searchValue, shouldFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, totalCount };
};
export const useHistorysData = () => {
  const [auditHistory, setAuditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true); // To track if there are more pages to load

  const fetchHistory = useCallback(async () => {
    if (loadingHistory || !hasMore) return; // Prevent multiple simultaneous fetches or fetching if no more data

    setLoadingHistory(true);
    try {
      const obfuscatedPath = getObfuscatedPath('history');
      const response = await request.get({ entity: obfuscatedPath, params: { page, pageSize: 10 } });
      const decryptedData = await response.result;
      if (decryptedData) {
        setAuditHistory((prevHistory) => [...prevHistory, ...decryptedData]);
        setHasMore(response.hasMore);
        setPage((prevPage) => prevPage + 1);
      } else {
        console.warn('No history data found or empty response:', response.result?.message || 'No response message');
        setHasMore(false); // No data, so no more to fetch
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      setHasMore(false); // On error, assume no more data can be fetched
    } finally {
      setLoadingHistory(false);
    }
  }, [page, loadingHistory, hasMore]); // Dependencies for useCallback

  useEffect(() => {
    fetchHistory();
  }, []); // Initial fetch when component mounts

  return { auditHistory, loadingHistory, hasMore, fetchHistory }; // Return fetchHistory to allow triggering more loads
};
export const useSubworkstreamData = (
  filterVisible = false,
  groupVisible = false,
  colorVisible = false,
  sortVisible = false,
  tableDownloadVisible = false
) => {
  const [workstreams, setWorkstreams] = useState([]);
  const [isLoadingWorkstreams, setIsLoadingWorkstreams] = useState(false);
  const [workstreamError, setWorkstreamError] = useState(null);

  const shouldFetchWorkstream = filterVisible || groupVisible || colorVisible || sortVisible || tableDownloadVisible;

  useEffect(() => {
    const fetchWorkstreamData = async () => {
      if (!shouldFetchWorkstream) {
        // Clear data and reset state if it's not visible and there's existing data or a pending load/error
        if (workstreams.length > 0 || isLoadingWorkstreams || workstreamError) {
          setWorkstreams([]);
          setIsLoadingWorkstreams(false);
          setWorkstreamError(null);
        }
        return; // Exit early if no fetch is required
      }

      setIsLoadingWorkstreams(true);
      setWorkstreamError(null); // Clear any previous errors

      try {
        const obfuscatedPath = getObfuscatedPath('subworkstream');
        const { result: workstreamResult } = await request.get({ entity: obfuscatedPath });
        const decryptedData = workstreamResult;
        setWorkstreams(decryptedData || []); // Ensure it's an array
      } catch (err) {
        console.error('Error fetching workstreams:', err);
        setWorkstreamError(err);
        setWorkstreams([]); // Ensure it's an array even on error
      } finally {
        setIsLoadingWorkstreams(false);
      }
    };

    fetchWorkstreamData();
  }, [shouldFetchWorkstream]); // Effect re-runs when 'shouldFetchWorkstream' changes

  return { workstreams, isLoadingWorkstreams, workstreamError };
};
export const useStrategyData = () => {
  const [strategies, setStrategies] = useState([]);
  const fetchData = async () => {
    try {
      const obfuscatedPath = getObfuscatedPath('strategy');
      const { result: strategyResult } = await request.get({ entity: obfuscatedPath });
      const decryptedData = strategyResult;
      setStrategies(decryptedData);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  return strategies;
};
export const useWorkstreamsData = () => {
  const [workstreams, setWorkstreams] = useState([]);
  const fetchWorkstreamData = async () => {
    try {
      const obfuscatedPath = getObfuscatedPath('workstream');
      const { result: workstreamResult } = await request.get({ entity: obfuscatedPath });
      const decryptedData = workstreamResult;
      setWorkstreams(decryptedData);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };
  useEffect(() => {
    fetchWorkstreamData();
  }, []);
  return workstreams;
};
export const useActionItemData = () => {
  const [actionItem, setActionItem] = useState([]);
  const itemData = async () => {
    try {
      const obfuscatedPath = getObfuscatedPath('workstreamaction');
      const { result: itemResult } = await request.get({ entity: obfuscatedPath });
      const decryptedData = itemResult;
      setActionItem(decryptedData || []);
    } catch (err) {
      console.error('Error fetching workstream action:', err);
      setActionItem([]);
    }
  };
  useEffect(() => {
    itemData();
  }, []);
  return actionItem;
};
export const useFilteredInitiatives = (initiatives) => {
  const [myInitiatives, setMyInitiatives] = useState([]);
  const currentUser = useSelector(selectAuth);
  useEffect(() => {
    if (initiatives) {
      const filteredInitiatives =
        initiatives &&
        initiatives.filter((item) => {
          if (item.initiativeName?.length > 40) {
            item.initiativeName = item.initiativeName.substring(0, 40) + '...';
          }
          const isOwner = item.initiativeOwnerInfo?.[0]?._id === currentUser.id;
          const isBackupOwner = item.initiativeOwnerBackupInfo?.[0]?._id === currentUser.id;
          const isWorkstreamLead = item.workstreamLeadInfo?.[0]?._id === currentUser.id;
          const isWorkstreamSponsor = item.workstreamSponsorInfo?.[0]?._id === currentUser.id;
          const isAllowedUser = item.AllowOnlyTo?.some(
            (allowedUser) => allowedUser._id === currentUser.id
          );
          const isAdminRole =
            currentUser.role.name === 'CTO' ||
            currentUser.role.name === 'TO Finance' ||
            currentUser.role.name === 'Strategy Analyst' ||
            currentUser.role.name === 'Admin';
          const hasAccess =
            isOwner ||
            isBackupOwner ||
            isWorkstreamLead ||
            isWorkstreamSponsor ||
            isAllowedUser ||
            isAdminRole;
          return hasAccess;
        });
      setMyInitiatives(filteredInitiatives);
    }
  }, [initiatives, currentUser]);
  return myInitiatives;
};
export const useMilestoneData = (myInitiatives, currentUser) => {
  const [milestoneCount, setMilestoneCount] = useState(0);
  const [milestones, setMilestones] = useState([]);

  useEffect(() => {
    let total = 0;
    let allMilestones = [];

    if (!myInitiatives || myInitiatives.length === 0 || !currentUser) {
      setMilestoneCount(0);
      setMilestones([]);
      return;
    }

    const isLeadershipOrAdmin =
      currentUser?.role?.name === "CTO" ||
      currentUser?.role?.name === "TO Finance" ||
      currentUser?.role?.name === "Strategy Analyst" ||
      currentUser?.role?.name === "Admin";

    myInitiatives.forEach((initiative) => {
      const isOwner = initiative.initiativeOwnerInfo?.[0]?._id === currentUser.id;
      const isBackupOwner = initiative.initiativeOwnerBackupInfo?.[0]?._id === currentUser.id;
      const isWorkstreamLead = initiative.workstreamLeadInfo?.[0]?._id === currentUser.id;
      const isWorkstreamSponsor = initiative.workstreamSponsorInfo?.[0]?._id === currentUser.id;
      const isAllowedUser = initiative.AllowOnlyTo?.some(
        (allowedUser) => allowedUser._id === currentUser.id
      );

      // ✅ FULL ACCESS USERS - Can see ALL milestones in the initiative
      const hasFullMilestoneView =
        isLeadershipOrAdmin ||
        isOwner ||
        isBackupOwner ||
        isWorkstreamLead ||
        isWorkstreamSponsor ||
        isAllowedUser;

      // CASE 1: Full access users see ALL milestones
      if (hasFullMilestoneView && initiative.milestones?.length) {
        const allInitiativeMilestones = initiative.milestones.map((m) => ({
          ...m,
          initiativeId: initiative._id,
          initiativeName: initiative.initiativeName,
        }));
        
        total += allInitiativeMilestones.length;
        allMilestones.push(...allInitiativeMilestones);
        return; // Skip milestone owner check
      }

      // CASE 2: Milestone Owners see ONLY milestones they own
      // This only applies if they DON'T have full access
      const ownedMilestones = initiative.milestones?.filter(
        (m) => m.Owner === currentUser.id
      ) || [];

      if (ownedMilestones.length > 0) {
        const mappedMilestones = ownedMilestones.map((m) => ({
          ...m,
          initiativeId: initiative._id,
          initiativeName: initiative.initiativeName,
        }));

        total += mappedMilestones.length;
        allMilestones.push(...mappedMilestones);
      }

      // CASE 3: No access - user sees nothing from this initiative
    });

    setMilestoneCount(total);
    setMilestones(allMilestones);
  }, [myInitiatives, currentUser]);

  return { milestoneCount, milestones };
};
export const useImpactData = (myInitiatives) => {
  const [impact, setImpact] = useState([]);
  useEffect(() => {
    let allImpact = [];
    if (myInitiatives && myInitiatives.length > 0) {
      myInitiatives.forEach((initiative) => {
        if (initiative.impact && initiative.impact.length > 0) {
          allImpact = allImpact.concat(initiative.impact.map((i) => ({ ...i })));
        }
      });
    }
    setImpact(allImpact);
  }, [myInitiatives]);
  return { impact };
};
export const useHistoryData = () => {
  const [historyData, setHistoryData] = useState([]);

  const fetchHistoryData = async () => {
    try {
      // 1. Get the obfuscated endpoint for 'history'
      const obfuscatedPath = getObfuscatedPath('history');

      // 2. Fetch the data from the API
      const response = await request.get({ entity: obfuscatedPath });

      // 3. Decrypt the result if it exists
      if (response?.result) {
        const decryptedData = response.result;
        setHistoryData(decryptedData);
        return decryptedData; // Return data in case it's needed for immediate use
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  useEffect(() => {
    fetchHistoryData();
  }, []);

  return historyData;
};